import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useFloating, autoUpdate, offset, flip, shift, size, FloatingPortal } from '@floating-ui/react';
import { useMsal } from '@azure/msal-react';
import {
  getWorkers, getProjects, getCars, getPlannerEntries, upsertPlannerEntry,
  addProject, updateProject, deleteProject, renameProjectInEntries,
  addWorker, updateWorker, deleteWorker, renameWorkerInEntries,
  addCar, updateCar, deleteCar, initCarListColumns, initProjectDateColumns,
  addChangeLog, getChangeLogs, ChangeLogEntry, deletePlannerEntry,
} from './graphService';
import { formatDate, formatDateCZ, getISOWeek, getWeekDates, getWeeksForMonth, getCzechHolidays, MONTH_NAMES, DAY_NAMES } from './utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Worker { id: string; Title: string; FullName: string; SortOrder: number; IsActive: boolean; }
export interface Project { id: string; Title: string; Color?: string; SortOrder: number; StartDate?: string; DeadlineDate?: string; }
export interface Car { id: string; Title: string; Color: string; TextColor?: string; SortOrder: number; Notes?: string; MultiSelect?: boolean; }
interface Entry {
  id?: string; WorkerId: number; WorkerName: string; EntryDate: string;
  ProjectNames: string; CarId: number; CarColor: string; Year: number; WeekNumber: number;
}
type EntryMap = Record<string, Entry>;

// ─── Constants ────────────────────────────────────────────────────────────────

const LR_GREEN = '#7bb23f';
const CELL_W = 104;
const NAME_W = 160;
const SEP = '; ';

function entryKey(workerName: string, date: string) { return `${workerName}|${date}`; }

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message }: { message: string }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 3000,
      background: '#222', color: '#fff', padding: '10px 22px',
      borderRadius: 8, fontSize: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
    }}>
      {message}
    </div>
  );
}

// ─── Modal wrapper ────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', minWidth: 520, maxWidth: 760, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#222' }}>{title}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 24, cursor: 'pointer', color: '#888', lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Shared table styles ──────────────────────────────────────────────────────

const TH: React.CSSProperties = { padding: '6px 10px', textAlign: 'left', fontSize: 12, color: '#666', fontWeight: 600, borderBottom: '2px solid #eee', whiteSpace: 'nowrap' };
const TD: React.CSSProperties = { padding: '5px 10px', fontSize: 13, borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };
const BTN = (color: string): React.CSSProperties => ({ padding: '4px 12px', background: color, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600 });
const INPUT: React.CSSProperties = { padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13, width: '100%', boxSizing: 'border-box' };

// ─── ProjectsModal ────────────────────────────────────────────────────────────

function ProjectsModal({ projects, onAdd, onUpdate, onDelete, onClose, onInitDateColumns }: {
  projects: Project[];
  onAdd: (name: string, sortOrder: number) => Promise<void>;
  onUpdate: (id: string, fields: { name: string; sortOrder: number; startDate?: string; deadlineDate?: string; oldName: string }) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onInitDateColumns: () => Promise<void>;
  onClose: () => void;
}) {
  const [newName, setNewName] = useState('');
  const [newSort, setNewSort] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editOldName, setEditOldName] = useState('');
  const [editName, setEditName] = useState('');
  const [editSort, setEditSort] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editDeadline, setEditDeadline] = useState('');
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [initDone, setInitDone] = useState(false);

  async function handleAdd() {
    if (!newName.trim() || busy) return;
    setBusy(true);
    try {
      await onAdd(newName.trim(), Number(newSort) || 0);
      setNewName(''); setNewSort('');
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveEdit() {
    if (!editId || !editName.trim() || busy) return;
    setBusy(true);
    setModalError(null);
    try {
      await onUpdate(editId, { name: editName.trim(), sortOrder: Number(editSort) || 0, startDate: editStart, deadlineDate: editDeadline, oldName: editOldName });
      setEditId(null);
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (busy || !window.confirm('Opravdu smazat tento projekt?')) return;
    setBusy(true);
    try {
      await onDelete(id);
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleInitDateColumns() {
    setBusy(true);
    setModalError(null);
    try {
      await onInitDateColumns();
      setInitDone(true);
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(p: Project) {
    setEditId(p.id);
    setEditOldName(p.Title);
    setEditName(p.Title);
    setEditSort(String(p.SortOrder || 0));
    setEditStart(p.StartDate ? p.StartDate.slice(0, 10) : '');
    setEditDeadline(p.DeadlineDate ? p.DeadlineDate.slice(0, 10) : '');
  }

  return (
    <Modal title="Správa projektů" onClose={onClose}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={TH}>Název projektu</th>
            <th style={{ ...TH, width: 70 }}>Pořadí</th>
            <th style={{ ...TH, width: 120 }}>Zahájení</th>
            <th style={{ ...TH, width: 120 }}>Deadline</th>
            <th style={TH}></th>
          </tr>
        </thead>
        <tbody>
          {projects.map(p => (
            <tr key={p.id} style={{ background: editId === p.id ? '#f7fbf2' : undefined }}>
              <td style={TD}>
                {editId === p.id
                  ? <input type="text" style={INPUT} value={editName} autoFocus
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditId(null); }} />
                  : p.Title
                }
              </td>
              <td style={TD}>
                {editId === p.id
                  ? <input style={{ ...INPUT, width: 60 }} value={editSort} type="number"
                      onChange={e => setEditSort(e.target.value)} />
                  : p.SortOrder || 0
                }
              </td>
              <td style={TD}>
                {editId === p.id
                  ? <input style={{ ...INPUT, width: 140 }} value={editStart} type="date"
                      onChange={e => setEditStart(e.target.value)} />
                  : p.StartDate ? p.StartDate.slice(0, 10) : <span style={{ color: '#ccc' }}>—</span>
                }
              </td>
              <td style={TD}>
                {editId === p.id
                  ? <input style={{ ...INPUT, width: 140 }} value={editDeadline} type="date"
                      onChange={e => setEditDeadline(e.target.value)} />
                  : p.DeadlineDate ? p.DeadlineDate.slice(0, 10) : <span style={{ color: '#ccc' }}>—</span>
                }
              </td>
              <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
                {editId === p.id
                  ? <>
                      <button style={BTN(LR_GREEN)} onClick={handleSaveEdit} disabled={busy}>Uložit</button>{' '}
                      <button style={BTN('#999')} onClick={() => setEditId(null)}>Zrušit</button>
                    </>
                  : <>
                      <button style={BTN('#555')} onClick={() => startEdit(p)} disabled={busy}>Upravit</button>{' '}
                      <button style={BTN('#e74c3c')} onClick={() => handleDelete(p.id)} disabled={busy}>Smazat</button>
                    </>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <input style={{ ...INPUT, flex: 2 }} value={newName} placeholder="Název nového projektu"
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()} />
        <input style={{ ...INPUT, width: 80 }} value={newSort} placeholder="Pořadí" type="number"
          onChange={e => setNewSort(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()} />
        <button style={BTN(LR_GREEN)} onClick={handleAdd} disabled={busy}>Přidat</button>
      </div>
      {modalError && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fde8e8', borderRadius: 6, color: '#c0392b', fontSize: 12 }}>
          {modalError}
        </div>
      )}
      <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 10 }}>
        <button style={{ ...BTN('#888'), fontSize: 11 }} onClick={handleInitDateColumns} disabled={busy || initDone}>
          {initDone ? 'Sloupce vytvořeny ✓' : 'Inicializovat datumové sloupce (1× po nasazení)'}
        </button>
      </div>
    </Modal>
  );
}

// ─── WorkersModal ─────────────────────────────────────────────────────────────

function WorkersModal({ workers, onAdd, onUpdate, onDelete, onClose }: {
  workers: Worker[];
  onAdd: (name: string, sortOrder: number) => Promise<void>;
  onUpdate: (id: string, fields: Partial<{ FullName: string; SortOrder: number; IsActive: boolean; oldName: string }>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onClose: () => void;
}) {
  const [newName, setNewName] = useState('');
  const [newSort, setNewSort] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editOldName, setEditOldName] = useState('');
  const [editName, setEditName] = useState('');
  const [editSort, setEditSort] = useState('');
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  async function handleAdd() {
    if (!newName.trim() || busy) return;
    setBusy(true);
    setModalError(null);
    try {
      await onAdd(newName.trim(), Number(newSort) || 0);
      setNewName(''); setNewSort('');
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveEdit() {
    if (!editId || !editName.trim() || busy) return;
    setBusy(true);
    setModalError(null);
    try {
      await onUpdate(editId, { FullName: editName.trim(), SortOrder: Number(editSort) || 0, oldName: editOldName });
      setEditId(null);
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleActive(w: Worker) {
    if (busy) return;
    setBusy(true);
    setModalError(null);
    try {
      await onUpdate(w.id, { IsActive: !w.IsActive });
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (busy || !window.confirm('Opravdu smazat tohoto pracovníka?')) return;
    setBusy(true);
    setModalError(null);
    try {
      await onDelete(id);
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Správa pracovníků" onClose={onClose}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={TH}>Jméno</th>
            <th style={{ ...TH, width: 70 }}>Pořadí</th>
            <th style={{ ...TH, width: 80 }}>Aktivní</th>
            <th style={TH}></th>
          </tr>
        </thead>
        <tbody>
          {workers.map(w => (
            <tr key={w.id} style={{ opacity: w.IsActive === false ? 0.5 : 1, background: editId === w.id ? '#f7fbf2' : undefined }}>
              <td style={TD}>
                {editId === w.id
                  ? <input type="text" style={INPUT} value={editName} autoFocus
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditId(null); }} />
                  : w.FullName || w.Title
                }
              </td>
              <td style={TD}>
                {editId === w.id
                  ? <input style={{ ...INPUT, width: 60 }} value={editSort} type="number"
                      onChange={e => setEditSort(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditId(null); }} />
                  : w.SortOrder || 0
                }
              </td>
              <td style={TD}>
                <button onClick={() => handleToggleActive(w)} disabled={busy}
                  style={{ ...BTN(w.IsActive !== false ? LR_GREEN : '#bbb'), padding: '3px 10px', fontSize: 11 }}>
                  {w.IsActive !== false ? 'Ano' : 'Ne'}
                </button>
              </td>
              <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
                {editId === w.id
                  ? <>
                      <button style={BTN(LR_GREEN)} onClick={handleSaveEdit} disabled={busy}>Uložit</button>{' '}
                      <button style={BTN('#999')} onClick={() => setEditId(null)}>Zrušit</button>
                    </>
                  : <>
                      <button style={BTN('#555')} onClick={() => { setEditId(w.id); setEditOldName(w.FullName || w.Title); setEditName(w.FullName || w.Title); setEditSort(String(w.SortOrder || 0)); }} disabled={busy}>Upravit</button>{' '}
                      <button style={BTN('#e74c3c')} onClick={() => handleDelete(w.id)} disabled={busy}>Smazat</button>
                    </>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <input style={{ ...INPUT, flex: 2 }} value={newName} placeholder="Jméno pracovníka"
          onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
        <input style={{ ...INPUT, width: 80 }} value={newSort} placeholder="Pořadí" type="number"
          onChange={e => setNewSort(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
        <button style={BTN(LR_GREEN)} onClick={handleAdd} disabled={busy}>Přidat</button>
      </div>
      {modalError && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fde8e8', borderRadius: 6, color: '#c0392b', fontSize: 12 }}>
          {modalError}
        </div>
      )}
    </Modal>
  );
}

// ─── CarsModal ────────────────────────────────────────────────────────────────

const EMPTY_CAR = { name: '', color: '#cccccc', textColor: '#000000', sortOrder: '', notes: '', multiSelect: false };

function CarsModal({ cars, instance, account, onAdd, onUpdate, onDelete, onClose, showToast }: {
  cars: Car[];
  instance: any; account: any;
  onAdd: (car: { name: string; color: string; textColor: string; sortOrder: number; notes: string; multiSelect: boolean }) => Promise<void>;
  onUpdate: (id: string, fields: Partial<{ Title: string; Color: string; TextColor: string; SortOrder: number; Notes: string; MultiSelect: boolean }>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onClose: () => void;
  showToast: (msg: string) => void;
}) {
  const [newCar, setNewCar] = useState(EMPTY_CAR);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; color: string; textColor: string; sortOrder: string; notes: string; multiSelect: boolean }>(EMPTY_CAR);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  function startEdit(c: Car) {
    setEditId(c.id);
    setEditForm({ name: c.Title, color: c.Color || '#cccccc', textColor: c.TextColor || '#000000', sortOrder: String(c.SortOrder || 0), notes: c.Notes || '', multiSelect: c.MultiSelect ?? false });
  }

  async function handleSaveEdit() {
    if (!editId || !editForm.name.trim() || busy) return;
    setBusy(true);
    setModalError(null);
    try {
      await onUpdate(editId, { Title: editForm.name, Color: editForm.color, TextColor: editForm.textColor, SortOrder: Number(editForm.sortOrder) || 0, Notes: editForm.notes, MultiSelect: editForm.multiSelect });
      setEditId(null);
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleAdd() {
    if (!newCar.name.trim() || busy) return;
    setBusy(true);
    setModalError(null);
    try {
      await onAdd({ ...newCar, sortOrder: Number(newCar.sortOrder) || 0, multiSelect: newCar.multiSelect });
      setNewCar(EMPTY_CAR);
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (busy || !window.confirm('Opravdu smazat toto auto?')) return;
    setBusy(true);
    setModalError(null);
    try {
      await onDelete(id);
    } catch (e: any) {
      setModalError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleInitColumns() {
    setBusy(true);
    await initCarListColumns(instance, account).catch(e => alert('Chyba: ' + e.message));
    showToast('Sloupce TextColor a Notes vytvořeny (nebo už existovaly)');
    setBusy(false);
  }

  const colInput = (value: string, onChange: (v: string) => void, type = 'text', placeholder = '') => (
    <input type={type} value={value} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      style={{ ...INPUT, width: type === 'color' ? 40 : type === 'number' ? 60 : undefined, padding: type === 'color' ? 1 : undefined }} />
  );

  return (
    <Modal title="Správa aut" onClose={onClose}>
      <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'flex-end' }}>
        <button style={{ ...BTN('#555'), fontSize: 11 }} onClick={handleInitColumns} disabled={busy}>
          Inicializovat sloupce Notes/TextColor
        </button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={TH}>Název</th>
            <th style={TH}>Pozadí</th>
            <th style={TH}>Text</th>
            <th style={{ ...TH, width: 60 }}>Pořadí</th>
            <th style={{ ...TH, width: 80 }}>Multi</th>
            <th style={TH}>Poznámky</th>
            <th style={TH}></th>
          </tr>
        </thead>
        <tbody>
          {cars.map(c => (
            <React.Fragment key={c.id}>
              <tr>
                <td style={TD}>{c.Title}</td>
                <td style={TD}><span style={{ display: 'inline-block', width: 20, height: 20, background: c.Color, border: '1px solid #ccc', borderRadius: 3, verticalAlign: 'middle' }} /></td>
                <td style={TD}><span style={{ display: 'inline-block', width: 20, height: 20, background: c.TextColor || '#000', border: '1px solid #ccc', borderRadius: 3, verticalAlign: 'middle' }} /></td>
                <td style={TD}>{c.SortOrder || 0}</td>
                <td style={TD}>{c.MultiSelect ? <span style={{ color: LR_GREEN, fontWeight: 700, fontSize: 12 }}>✓</span> : <span style={{ color: '#ccc', fontSize: 12 }}>—</span>}</td>
                <td style={{ ...TD, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#888', fontSize: 12 }}>{c.Notes || '—'}</td>
                <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {editId === c.id
                    ? null
                    : <><button style={BTN('#555')} onClick={() => startEdit(c)}>Upravit</button>{' '}
                        <button style={BTN('#e74c3c')} onClick={() => handleDelete(c.id)}>Smazat</button></>
                  }
                </td>
              </tr>
              {editId === c.id && (
                <tr style={{ background: '#f9f9f9' }}>
                  <td colSpan={7} style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666' }}>
                        Název{colInput(editForm.name, v => setEditForm(f => ({ ...f, name: v })))}
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666' }}>
                        Pozadí{colInput(editForm.color, v => setEditForm(f => ({ ...f, color: v })), 'color')}
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666' }}>
                        Barva textu{colInput(editForm.textColor, v => setEditForm(f => ({ ...f, textColor: v })), 'color')}
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666' }}>
                        Pořadí{colInput(editForm.sortOrder, v => setEditForm(f => ({ ...f, sortOrder: v })), 'number')}
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#666', cursor: 'pointer', marginTop: 14 }}>
                        <input type="checkbox" checked={editForm.multiSelect} onChange={e => setEditForm(f => ({ ...f, multiSelect: e.target.checked }))} />
                        Lze přiřadit více pracovníkům
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666', flex: 1 }}>
                        Poznámky (servis, závady)
                        <textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                          rows={3} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }} />
                      </label>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                      <button style={BTN(LR_GREEN)} onClick={handleSaveEdit} disabled={busy}>Uložit</button>
                      <button style={BTN('#999')} onClick={() => setEditId(null)}>Zrušit</button>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 16, padding: '14px 16px', background: '#f9f9f9', borderRadius: 8, border: '1px solid #eee' }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#444' }}>Přidat nové auto</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666', flex: 2 }}>
            Název<input style={INPUT} value={newCar.name} placeholder="Název auta" onChange={e => setNewCar(f => ({ ...f, name: e.target.value }))} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666' }}>
            Pozadí<input type="color" value={newCar.color} onChange={e => setNewCar(f => ({ ...f, color: e.target.value }))}
              style={{ width: 40, height: 30, padding: 1, border: '1px solid #ccc', borderRadius: 4 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666' }}>
            Barva textu<input type="color" value={newCar.textColor} onChange={e => setNewCar(f => ({ ...f, textColor: e.target.value }))}
              style={{ width: 40, height: 30, padding: 1, border: '1px solid #ccc', borderRadius: 4 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666' }}>
            Pořadí<input type="number" style={{ ...INPUT, width: 70 }} value={newCar.sortOrder} onChange={e => setNewCar(f => ({ ...f, sortOrder: e.target.value }))} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666', flex: 2 }}>
            Poznámky<textarea rows={2} style={{ ...INPUT, resize: 'vertical', fontFamily: 'inherit' }}
              value={newCar.notes} onChange={e => setNewCar(f => ({ ...f, notes: e.target.value }))} />
          </label>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#444', cursor: 'pointer', marginTop: 8 }}>
          <input type="checkbox" checked={newCar.multiSelect} onChange={e => setNewCar(f => ({ ...f, multiSelect: e.target.checked }))} />
          Lze přiřadit více pracovníkům ve stejný den
        </label>
        <button style={{ ...BTN(LR_GREEN), marginTop: 10 }} onClick={handleAdd} disabled={busy}>Přidat auto</button>
      </div>
      {modalError && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fde8e8', borderRadius: 6, color: '#c0392b', fontSize: 12 }}>
          {modalError}
        </div>
      )}
    </Modal>
  );
}

// ─── Car context menu (pravý klik) ────────────────────────────────────────────

function CarContextMenu({ cars, x, y, hasClipboard, stickyTop, onCopy, onPaste, onStartSelection, onSelect, onClose }: {
  cars: Car[]; x: number; y: number;
  hasClipboard: boolean;
  stickyTop: number;
  onCopy: () => void; onPaste: () => void; onStartSelection: () => void;
  onSelect: (car: Car | null) => void;
  onClose: () => void;
}) {
  const padding = useMemo(() => ({ top: stickyTop + 8, bottom: 8, left: 8, right: 8 }), [stickyTop]);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip({ padding }),
      shift({ padding }),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(50, availableHeight)}px`,
            overflowY: 'auto',
          });
        },
        padding,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    refs.setReference({
      getBoundingClientRect: () => ({ x, y, top: y, bottom: y, left: x, right: x, width: 0, height: 0 } as DOMRect),
    });
  }, [x, y, refs]);

  useEffect(() => {
    const mouseHandler = (e: MouseEvent) => {
      if (refs.floating.current && !refs.floating.current.contains(e.target as Node)) onClose();
    };
    const keyHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', mouseHandler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', mouseHandler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose, refs]);

  const itemStyle: React.CSSProperties = { padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: '#222', display: 'flex', alignItems: 'center', gap: 8 };

  return (
    <FloatingPortal>
      <div ref={refs.setFloating} style={{
        ...floatingStyles,
        zIndex: 2000,
        background: '#fff', border: '1px solid #ddd', borderRadius: 8,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 180, padding: '4px 0',
      }}>
        <div onClick={() => { onCopy(); onClose(); }} style={itemStyle}>Kopírovat</div>
        {hasClipboard && (
          <div onClick={() => { onPaste(); onClose(); }} style={itemStyle}>Vložit</div>
        )}
        <div onClick={() => { onStartSelection(); onClose(); }} style={itemStyle}>Vybrat pro hromadné vložení</div>
        <div style={{ borderTop: '1px solid #f0f0f0', margin: '4px 0' }} />
        <div style={{ padding: '4px 12px 6px', fontSize: 11, color: '#888', borderBottom: '1px solid #f0f0f0' }}>Vybrat auto</div>
        <div onClick={() => { onSelect(null); onClose(); }}
          style={{ padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: '#999', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, border: '1px solid #ddd', display: 'inline-block', background: '#fff' }} />
          — žádné auto —
        </div>
        {cars.length === 0
          ? <div style={{ padding: '7px 14px', fontSize: 12, color: '#bbb', fontStyle: 'italic' }}>Všechna auta jsou obsazena</div>
          : cars.map(c => (
              <div key={c.id} onClick={() => { onSelect(c); onClose(); }}
                style={{ padding: '7px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 14, height: 14, borderRadius: 3, background: c.Color, border: '1px solid rgba(0,0,0,0.1)', display: 'inline-block', flexShrink: 0 }} />
                {c.Title}
              </div>
            ))
        }
      </div>
    </FloatingPortal>
  );
}

// ─── Project picker ───────────────────────────────────────────────────────────

function ProjectPicker({ referenceEl, stickyTop, projects, selected, onConfirm, onClose }: {
  referenceEl: Element | null;
  stickyTop: number;
  projects: Project[]; selected: string[];
  onConfirm: (names: string[]) => void; onClose: () => void;
}) {
  const [checked, setChecked] = useState<Set<string>>(new Set(selected));
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const padding = useMemo(() => ({ top: stickyTop + 8, bottom: 8, left: 8, right: 8 }), [stickyTop]);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip({ padding }),
      shift({ padding }),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(100, availableHeight)}px`,
          });
        },
        padding,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    refs.setReference(referenceEl as Element | null);
  }, [referenceEl, refs]);

  useEffect(() => {
    const mouseHandler = (e: MouseEvent) => {
      const fl = refs.floating.current;
      if (fl && !fl.contains(e.target as Node) &&
          referenceEl && !referenceEl.contains(e.target as Node)) {
        onClose();
      }
    };
    const keyHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', mouseHandler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', mouseHandler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose, refs, referenceEl]);

  useEffect(() => { searchRef.current?.focus(); }, []);

  function toggle(name: string) {
    setChecked(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  }

  const knownNames = new Set(projects.map(p => p.Title));
  const orphanNames = selected.filter(n => n && !knownNames.has(n));
  const filtered = search ? projects.filter(p => p.Title.toLowerCase().includes(search.toLowerCase())) : projects;

  return (
    <FloatingPortal>
      <div ref={refs.setFloating} style={{
        ...floatingStyles,
        zIndex: 500,
        background: '#fff', border: '1px solid #ccc',
        borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        minWidth: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '6px 10px', borderBottom: '1px solid #eee', flexShrink: 0 }}>
          <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)} style={{ ...INPUT, fontSize: 12 }} />
        </div>
        <div style={{ borderBottom: '1px solid #eee', padding: '6px 14px', display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={() => { onConfirm(Array.from(checked)); onClose(); }}
            style={{ flex: 1, background: LR_GREEN, color: '#fff', border: 'none', borderRadius: 5, padding: '5px 0', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            OK
          </button>
          <button onClick={onClose}
            style={{ flex: 1, background: '#eee', border: 'none', borderRadius: 5, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
            Zrušit
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '4px 0' }}>
          {orphanNames.map(name => (
            <label key={`orphan-${name}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 14px', cursor: 'pointer', fontSize: 13, background: '#fff8f8' }}>
              <input type="checkbox" checked={checked.has(name)} onChange={() => toggle(name)} />
              <span style={{ color: '#c0392b' }}>{name}</span>
              <span style={{ fontSize: 10, color: '#e74c3c', fontStyle: 'italic', marginLeft: 2 }}>(neexistující projekt)</span>
            </label>
          ))}
          {orphanNames.length > 0 && <div style={{ borderBottom: '1px solid #f0f0f0', margin: '2px 0' }} />}
          {filtered.map(p => (
            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 14px', cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={checked.has(p.Title)} onChange={() => toggle(p.Title)} />
              {p.Color && <span style={{ width: 11, height: 11, borderRadius: 2, background: p.Color, display: 'inline-block', flexShrink: 0 }} />}
              {p.Title}
            </label>
          ))}
        </div>
      </div>
    </FloatingPortal>
  );
}

// ─── PlannerCell ──────────────────────────────────────────────────────────────

function PlannerCell({ entry, projects, cars, isWeekend, isReadOnly, selectionMode, isSelected, stickyTop, onToggleSelect, onProjectsChange, onContextMenu }: {
  entry: Entry | undefined;
  projects: Project[];
  cars: Car[];
  isWeekend: boolean;
  isReadOnly?: boolean;
  selectionMode: boolean;
  isSelected: boolean;
  stickyTop: number;
  onToggleSelect: () => void;
  onProjectsChange: (names: string[]) => void;
  onContextMenu: (x: number, y: number) => void;
}) {
  const [showProjects, setShowProjects] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);

  const car = entry?.CarId ? cars.find(c => Number(c.id) === entry.CarId) : undefined;
  const bgColor = car?.Color || (isWeekend ? '#f0f0f0' : '#fff');
  const textColor = car?.TextColor || '#222';
  const projectLines = entry?.ProjectNames ? entry.ProjectNames.split(SEP).filter(Boolean) : [];
  const selectedNames = entry?.ProjectNames ? entry.ProjectNames.split(SEP).filter(Boolean) : [];

  function handleClick() {
    if (isReadOnly) return;
    if (selectionMode) { onToggleSelect(); return; }
    setShowProjects(true);
  }

  const cursor = isReadOnly ? 'default' : selectionMode ? 'crosshair' : 'pointer';

  return (
    <div ref={cellRef} style={{ position: 'relative', width: CELL_W, minHeight: 32, background: bgColor, borderRight: '1px solid #ddd' }}
      onContextMenu={e => { e.preventDefault(); if (!isReadOnly) onContextMenu(e.clientX, e.clientY); }}>
      <div onClick={handleClick} style={{ padding: '3px 6px', minHeight: 32, cursor, userSelect: 'none' }}>
        {projectLines.length > 0
          ? projectLines.map((line, i) => (
              <div key={i} style={{ fontSize: 11, color: textColor, lineHeight: '15px', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {line}
              </div>
            ))
          : <span style={{ fontSize: 11, color: '#ccc' }}>{isReadOnly || isWeekend ? '' : (selectionMode ? '' : '+')}</span>
        }
      </div>
      {isSelected && (
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'rgba(123,178,63,0.22)', outline: `2px solid ${LR_GREEN}`, outlineOffset: '-2px',
        }} />
      )}
      {!isReadOnly && !selectionMode && showProjects && (
        <ProjectPicker
          referenceEl={cellRef.current}
          stickyTop={stickyTop}
          projects={projects}
          selected={selectedNames}
          onConfirm={onProjectsChange}
          onClose={() => setShowProjects(false)}
        />
      )}
    </div>
  );
}

// ─── PrintModal ───────────────────────────────────────────────────────────────

function formatDateFullCZ(d: Date) {
  return `${d.getUTCDate()}.${d.getUTCMonth() + 1}.${d.getUTCFullYear()}`;
}

function getWeeksOfYear(year: number) {
  const result: { week: number; dates: Date[] }[] = [];
  for (let w = 1; w <= 53; w++) {
    const dates = getWeekDates(year, w);
    // ISO rule: a week belongs to the year whose Thursday it contains
    if (dates[3].getUTCFullYear() === year) result.push({ week: w, dates });
  }
  return result;
}

function buildPrintHTML(
  week: number, dates: Date[], workers: Worker[], cars: Car[],
  weekEntries: EntryMap, holidays: Set<string>,
): string {
  const from = formatDateFullCZ(dates[0]);
  const to   = formatDateFullCZ(dates[6]);

  const carLegend = cars.map(c =>
    `<span style="display:inline-flex;align-items:center;gap:4px;margin-right:10px">
       <span style="width:12px;height:12px;border-radius:2px;background:${c.Color};border:1px solid #ccc;display:inline-block"></span>
       ${c.Title}
     </span>`
  ).join('');

  const activeWorkers = workers.filter(w => w.IsActive !== false);

  const headCells = dates.map((d, i) => {
    const ds  = formatDate(d);
    const wkd = i >= 5;
    const hol = holidays.has(ds);
    return `<th style="background:${wkd ? '#e8e8e8' : '#f0f0f0'};border:1px solid #ccc;padding:2px 3px;text-align:center;font-size:9px;font-weight:${wkd ? 400 : 700}">
      ${DAY_NAMES[i].slice(0, 2)}<br/><span style="font-weight:400">${formatDateCZ(d)}</span>${hol ? '<br/><span style="color:#e74c3c;font-size:8px">svátek</span>' : ''}
    </th>`;
  }).join('');

  const bodyRows = activeWorkers.map((worker, ri) => {
    const wName = worker.FullName || worker.Title;
    const rowBg = ri % 2 === 0 ? '#fff' : '#fafafa';
    const cells = dates.map((d, i) => {
      const ds  = formatDate(d);
      const wkd = i >= 5;
      const entry = weekEntries[entryKey(wName, ds)];
      const car   = entry?.CarId ? cars.find(c => Number(c.id) === entry.CarId) : undefined;
      const bg    = car?.Color || (wkd ? '#efefef' : rowBg);
      const tc    = car?.TextColor || '#222';
      const lines = entry?.ProjectNames ? entry.ProjectNames.split(SEP).filter(Boolean) : [];
      const content = lines.map(l => `<div style="font-size:8px;line-height:12px;color:${tc}">${l}</div>`).join('');
      return `<td style="border:1px solid #ddd;background:${bg};padding:2px 3px;vertical-align:top">${content}</td>`;
    }).join('');
    return `<tr>
      <td style="border:1px solid #ccc;padding:2px 6px;font-size:10px;font-weight:500;white-space:nowrap;background:${rowBg}">${wName}</td>
      ${cells}
    </tr>`;
  }).join('');

  return `<!DOCTYPE html><html><head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>Lee Row — Týden ${week}</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 10px; }
  h1 { font-size: 13px; font-weight: 700; color: #7bb23f; margin-bottom: 4px; }
  .legend { margin-bottom: 5px; font-size: 10px; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  td, th { overflow: hidden; word-break: break-word; }
  @media print {
    @page { size: A4 landscape; margin: 8mm; }
    html, body { width: 297mm; }
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
</style>
</head><body>
  <h1>Lee Row s.r.o. — Týden ${week} — ${from} – ${to}</h1>
  <div class="legend">${carLegend}</div>
  <table>
    <colgroup>
      <col style="width:130px"/>
      ${dates.map(() => '<col/>').join('')}
    </colgroup>
    <thead><tr>
      <th style="background:#f0f0f0;border:1px solid #ccc;padding:2px 6px;text-align:left;font-size:9px">Pracovník</th>
      ${headCells}
    </tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body></html>`;
}

function PrintModal({ workers, cars, entries, holidays, instance, account, year, onClose }: {
  workers: Worker[]; cars: Car[]; entries: EntryMap; holidays: Set<string>;
  instance: any; account: any; year: number; onClose: () => void;
}) {
  const currentWeek = getISOWeek(new Date());
  const allWeeks = getWeeksOfYear(year);
  const [selectedWeek, setSelectedWeek] = useState<number>(currentWeek);
  const [printing, setPrinting] = useState(false);

  async function handlePrint() {
    const wb = allWeeks.find(w => w.week === selectedWeek);
    if (!wb) return;
    setPrinting(true);
    const dateStrings = wb.dates.map(d => formatDate(d));
    let weekEntries: EntryMap = {};
    try {
      const fetched = await getPlannerEntries(instance, account, dateStrings);
      fetched.forEach((e: Entry) => { weekEntries[entryKey(e.WorkerName, e.EntryDate)] = e; });
    } catch { /* use empty entries */ }
    const html = buildPrintHTML(wb.week, wb.dates, workers, cars, weekEntries, holidays);
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      // Safari needs the document fully parsed before print() is called;
      // also requires onafterprint to close because Safari's print dialog
      // is asynchronous and win.close() right after print() races against it.
      const doClose = () => { try { win.close(); } catch { /* ignore */ } };
      win.onafterprint = doClose;
      setTimeout(() => {
        win.focus();
        win.print();
        // Fallback close for browsers that don't fire onafterprint (old Safari)
        setTimeout(doClose, 2000);
      }, 800);
    }
    setPrinting(false);
    onClose();
  }

  return (
    <Modal title="Tisk týdne" onClose={onClose}>
      <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>Vyber týden pro tisk (A4, na šířku):</div>
      <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
        {allWeeks.map(({ week, dates }) => {
          const isCurrent = week === currentWeek;
          const isSelected = week === selectedWeek;
          const label = `Týden ${week}  —  ${formatDateFullCZ(dates[0])} – ${formatDateFullCZ(dates[6])}`;
          return (
            <div key={week} onClick={() => setSelectedWeek(week)} style={{
              padding: '8px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10,
              background: isSelected ? '#eef8dc' : 'transparent',
              borderLeft: isSelected ? `3px solid ${LR_GREEN}` : '3px solid transparent',
              fontWeight: isCurrent ? 700 : 400,
              color: isCurrent ? LR_GREEN : '#333',
              borderBottom: '1px solid #f5f5f5',
            }}>
              {label}
              {isCurrent && <span style={{ fontSize: 11, background: LR_GREEN, color: '#fff', padding: '1px 7px', borderRadius: 10 }}>(aktuální)</span>}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '7px 18px', border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Zrušit</button>
        <button onClick={handlePrint} disabled={printing} style={{ padding: '7px 20px', background: LR_GREEN, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          {printing ? 'Připravuji…' : 'Tisknout'}
        </button>
      </div>
    </Modal>
  );
}

// ─── HistoryDrawer ────────────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  set: 'Nastaveno',
  clear: 'Vymazáno',
  'rename-project': 'Přejmenován projekt',
  'rename-worker': 'Přejmenován pracovník',
  'rename-result': 'Výsledek propagace',
  'add-project': 'Přidán projekt',
  'delete-project': 'Smazán projekt',
  'add-worker': 'Přidán pracovník',
  'delete-worker': 'Smazán pracovník',
  'add-car': 'Přidáno auto',
  'delete-car': 'Smazáno auto',
  'car-assign': 'Přiřazeno auto',
  'car-clear': 'Odebráno auto',
  paste: 'Vloženo',
  'bulk-paste': 'Hromadné vložení',
};

function HistoryDrawer({ open, instance, account, onClose }: {
  open: boolean; instance: any; account: any; onClose: () => void;
}) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState('');

  useEffect(() => {
    if (!open || !account) return;
    setLoading(true);
    getChangeLogs(instance, account)
      .then(setLogs)
      .catch(e => console.error('[HistoryDrawer]', e))
      .finally(() => setLoading(false));
  }, [open, instance, account]);

  const users = useMemo(() => Array.from(new Set(logs.map((l: any) => l.UserName).filter(Boolean))), [logs]);

  const filtered = useMemo(() => logs
    .filter((l: any) => !userFilter || l.UserName === userFilter)
    .filter((l: any) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (l.WorkerName || '').toLowerCase().includes(q)
        || (l.OldValue || '').toLowerCase().includes(q)
        || (l.NewValue || '').toLowerCase().includes(q)
        || (l.UserName || '').toLowerCase().includes(q);
    }),
  [logs, search, userFilter]);

  return (
    <>
      {open && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1499, background: 'rgba(0,0,0,0.18)' }}
          onClick={onClose}
        />
      )}
      <div style={{
        position: 'fixed', top: 0, right: open ? 0 : -410, bottom: 0, width: 400,
        background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.18)', zIndex: 1500,
        transition: 'right 0.25s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '16px 16px 12px', borderBottom: '2px solid #eee', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 17, flex: 1, color: '#222' }}>Historie změn</span>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 26, cursor: 'pointer', color: '#999', lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Hledat (projekt, pracovník, uživatel)…"
            style={{ ...INPUT, fontSize: 13 }}
          />
          <select value={userFilter} onChange={e => setUserFilter(e.target.value)} style={{ ...INPUT, fontSize: 13 }}>
            <option value="">Všichni uživatelé</option>
            {users.map(u => <option key={String(u)} value={String(u)}>{String(u)}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#aaa', fontSize: 14 }}>Načítám historii…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#ccc', fontSize: 13 }}>Žádné záznamy</div>
          ) : filtered.map((log: any, i: number) => (
            <div key={log.id || i} style={{ padding: '10px 16px', borderBottom: '1px solid #f5f5f5' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>{log.UserName || '—'}</span>
                <span style={{ color: '#bbb', fontSize: 11 }}>
                  {log.Timestamp
                    ? new Date(log.Timestamp).toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : ''}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 3 }}>
                <span style={{ fontWeight: 600 }}>{ACTION_LABELS[log.Action] || log.Action}</span>
                {log.WorkerName && <span style={{ color: '#777' }}> · {log.WorkerName}</span>}
                {log.EntryDate && (
                  <span style={{ color: '#aaa', fontSize: 11 }}> · {String(log.EntryDate).slice(0, 10)}</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12 }}>
                <span style={{
                  color: '#e74c3c', maxWidth: 155, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontStyle: log.OldValue ? 'normal' : 'italic',
                }}>
                  {log.OldValue || '(prázdné)'}
                </span>
                <span style={{ color: '#ccc', flexShrink: 0 }}>→</span>
                <span style={{
                  color: LR_GREEN, maxWidth: 155, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontStyle: log.NewValue ? 'normal' : 'italic',
                }}>
                  {log.NewValue || '(prázdné)'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Main Planner ─────────────────────────────────────────────────────────────

export default function Planner({ isReadOnly = false }: { isReadOnly?: boolean }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const today = new Date();
  const [year] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const holidays = getCzechHolidays(year);

  const [workers, setWorkers] = useState<Worker[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [cars, setCars] = useState<Car[]>([]);
  const [entries, setEntries] = useState<EntryMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const topbarRef = useRef<HTMLDivElement>(null);
  const [stickyTop, setStickyTop] = useState(0);

  const [renameRetry, setRenameRetry] = useState<{ type: 'project' | 'worker'; oldName: string; newName: string; failed: number } | null>(null);

  const [clipboard, setClipboard] = useState<{ ProjectNames: string; CarId: number; CarColor: string } | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<{ workerId: string; workerName: string; date: string; weekNumber: number; x: number; y: number; availableCars: Car[] } | null>(null);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [showWorkersModal, setShowWorkersModal] = useState(false);
  const [showCarsModal, setShowCarsModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);

  function showToast(msg: string) {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  }

  function logChange(entry: Omit<ChangeLogEntry, 'UserName' | 'UserEmail' | 'Timestamp'>) {
    if (!account) return;
    addChangeLog(instance, account, {
      ...entry,
      UserName: account.name || account.username,
      UserEmail: account.username,
      Timestamp: new Date().toISOString(),
    }).catch(e => console.warn('[logChange]', e));
  }

  // Load static data once
  useEffect(() => {
    if (!account) return;
    Promise.all([getWorkers(instance, account), getProjects(instance, account), getCars(instance, account)])
      .then(([w, p, c]) => { setWorkers(w); setProjects(p); setCars(c); })
      .catch(e => setError(String(e)));
  }, [instance, account]);

  // Load entries when month changes
  const loadEntries = useCallback(async (m: number, silent = false) => {
    if (!account) return;
    if (!silent) setLoading(true);
    try {
      // Collect every date string in every week that overlaps this month
      const weeks = getWeeksForMonth(year, m);
      const allDates: string[] = [];
      weeks.forEach(w => {
        const wYear = (m === 12 && w === 1) ? year + 1 : (m === 1 && w > 50) ? year - 1 : year;
        getWeekDates(wYear, w).forEach(d => allDates.push(formatDate(d)));
      });
      // Single API call, client-side filter by EntryDate
      const allEntries = await getPlannerEntries(instance, account, allDates);
      const map: EntryMap = {};
      allEntries.forEach((e: Entry) => {
        map[entryKey(e.WorkerName, e.EntryDate)] = e;
      });
      setEntries(map);
    } catch (e) {
      setError(String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [instance, account, year]);

  useEffect(() => { loadEntries(month); }, [loadEntries, month]);

  useEffect(() => {
    if (!account) return;
    const id = setInterval(async () => {
      const [, wRes, pRes, cRes] = await Promise.allSettled([
        loadEntries(month, true),
        getWorkers(instance, account),
        getProjects(instance, account),
        getCars(instance, account),
      ]);
      if (wRes.status === 'fulfilled') setWorkers(wRes.value);
      if (pRes.status === 'fulfilled') setProjects(pRes.value);
      if (cRes.status === 'fulfilled') setCars(cRes.value);
    }, 30000);
    return () => clearInterval(id);
  }, [loadEntries, month, account, instance]);

  useEffect(() => {
    const el = topbarRef.current;
    if (!el) return;
    const measure = () => setStickyTop(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ─── Entry mutations ──────────────────────────────────────────────────────

  async function fetchFreshProjects(): Promise<Set<string> | null> {
    try {
      const fresh = await getProjects(instance, account);
      setProjects(fresh);
      return new Set(fresh.map((p: any) => p.Title as string));
    } catch {
      return null;
    }
  }

  async function handleProjectsChange(worker: Worker, date: string, weekNumber: number, names: string[]) {
    const wName = worker.FullName || worker.Title;
    const key = entryKey(wName, date);
    const existing = entries[key];
    const projectNames = names.join(SEP);
    setSaving(true);
    try {
      // Validace: nové názvy (ne již existující osiřelé) musí být z aktuálního seznamu
      const existingNames = new Set((existing?.ProjectNames || '').split(SEP).filter(Boolean).map((n: string) => n.trim()));
      const addedNames = names.filter(n => !existingNames.has(n.trim()));
      if (addedNames.length > 0) {
        const freshNameSet = await fetchFreshProjects();
        if (freshNameSet) {
          const invalid = addedNames.filter(n => !freshNameSet.has(n));
          if (invalid.length > 0) {
            setError(`Projekt „${invalid.join('", „')}" už neexistuje nebo byl přejmenován. Seznam projektů byl obnoven, vyberte znovu.`);
            return;
          }
        }
      }

      if (names.length === 0 && !existing?.CarId) {
        if (existing?.id) {
          await deletePlannerEntry(instance, account, existing.id);
          setEntries(prev => { const n = { ...prev }; delete n[key]; return n; });
        }
      } else {
        const entryYear = new Date(date).getFullYear();
        const payload = {
          id: existing?.id, WorkerId: Number(worker.id),
          WorkerName: wName, EntryDate: date,
          ProjectNames: projectNames, CarId: existing?.CarId ?? 0,
          CarColor: existing?.CarColor ?? '', Year: entryYear, WeekNumber: weekNumber,
        };
        const newId = await upsertPlannerEntry(instance, account, payload);
        setEntries(prev => ({ ...prev, [key]: { ...payload, id: newId } }));
      }
      logChange({
        Action: names.length === 0 ? 'clear' : 'set',
        WorkerName: wName,
        EntryDate: date,
        OldValue: existing?.ProjectNames ?? '',
        NewValue: projectNames,
      });
      showToast('Uloženo');
    } catch (e) { setError(String(e)); } finally { setSaving(false); }
  }

  async function handleCarChange(workerId: string, workerName: string, date: string, weekNumber: number, car: Car | null) {
    const key = entryKey(workerName, date);
    const existing = entries[key];
    setSaving(true);
    try {
      const entryYear = new Date(date).getFullYear();
      const oldCar = existing?.CarId ? cars.find(c => Number(c.id) === existing.CarId) : null;
      if (!car && !existing?.ProjectNames) {
        // Auto odebráno a žádné projekty — smaž záznam pokud existuje
        if (existing?.id) {
          await deletePlannerEntry(instance, account, existing.id);
          setEntries(prev => { const n = { ...prev }; delete n[key]; return n; });
        }
      } else {
        const payload = {
          id: existing?.id, WorkerId: Number(workerId),
          WorkerName: workerName, EntryDate: date,
          ProjectNames: existing?.ProjectNames ?? '',
          CarId: car ? Number(car.id) : 0,
          CarColor: car?.Color ?? '',
          Year: entryYear, WeekNumber: weekNumber,
        };
        const newId = await upsertPlannerEntry(instance, account, payload);
        setEntries(prev => ({ ...prev, [key]: { ...payload, id: newId } }));
      }
      logChange({
        Action: car ? 'car-assign' : 'car-clear',
        WorkerName: workerName,
        EntryDate: date,
        OldValue: oldCar?.Title ?? '',
        NewValue: car?.Title ?? '',
      });
      showToast(car ? 'Auto přiřazeno' : 'Auto odebráno');
    } catch (e) { setError(String(e)); } finally { setSaving(false); }
  }

  // ─── Clipboard copy/paste ─────────────────────────────────────────────────

  function handleCopy() {
    if (!contextMenu) return;
    const entry = entries[entryKey(contextMenu.workerName, contextMenu.date)];
    setClipboard({ ProjectNames: entry?.ProjectNames ?? '', CarId: entry?.CarId ?? 0, CarColor: entry?.CarColor ?? '' });
    showToast('Zkopírováno');
  }

  async function handlePaste() {
    if (!contextMenu || !clipboard) return;

    // Validace názvů ze schránky
    if (clipboard.ProjectNames) {
      const clipNames = clipboard.ProjectNames.split(SEP).filter(Boolean).map(n => n.trim());
      if (clipNames.length > 0) {
        const freshNameSet = await fetchFreshProjects();
        if (freshNameSet) {
          const invalid = clipNames.filter(n => !freshNameSet.has(n));
          if (invalid.length > 0) {
            setError(`Schránka obsahuje neplatné projekty: „${invalid.join('", „')}". Zkopírujte buňku znovu.`);
            return;
          }
        }
      }
    }

    const key = entryKey(contextMenu.workerName, contextMenu.date);
    const existing = entries[key];
    setSaving(true);
    try {
      const entryYear = new Date(contextMenu.date).getFullYear();
      const payload = {
        id: existing?.id, WorkerId: Number(contextMenu.workerId),
        WorkerName: contextMenu.workerName, EntryDate: contextMenu.date,
        ProjectNames: clipboard.ProjectNames, CarId: clipboard.CarId,
        CarColor: clipboard.CarColor, Year: entryYear, WeekNumber: contextMenu.weekNumber,
      };
      const newId = await upsertPlannerEntry(instance, account, payload);
      setEntries(prev => ({ ...prev, [key]: { ...payload, id: newId } }));
      logChange({
        Action: 'paste',
        WorkerName: contextMenu.workerName,
        EntryDate: contextMenu.date,
        OldValue: existing?.ProjectNames ?? '',
        NewValue: clipboard.ProjectNames,
      });
      showToast('Vloženo');
    } catch (e) { setError(String(e)); } finally { setSaving(false); }
  }

  // ─── Project modal handlers ───────────────────────────────────────────────

  async function handleAddProject(name: string, sortOrder: number) {
    await addProject(instance, account, name, sortOrder);
    const fresh = await getProjects(instance, account);
    setProjects(fresh);
    showToast('Projekt přidán');
    try { logChange({ Action: 'add-project', WorkerName: '', EntryDate: '', OldValue: '', NewValue: name }); } catch (e) { console.warn('[logChange add-project]', e); }
  }

  async function handleUpdateProject(id: string, fields: { name: string; sortOrder: number; startDate?: string; deadlineDate?: string; oldName: string }) {
    const toSP = (d?: string) => d ? `${d}T00:00:00Z` : null;
    const oldName = fields.oldName.trim();
    const isRename = !!oldName && oldName !== fields.name.trim();
    console.log(`[handleUpdateProject] id=${id} oldName="${oldName}" newName="${fields.name}" isRename=${isRename}`);

    const spFields: any = {
      Title: fields.name,
      SortOrder: fields.sortOrder,
      StartDate: toSP(fields.startDate),
      DeadlineDate: toSP(fields.deadlineDate),
    };
    await updateProject(instance, account, id, spFields);

    if (isRename) {
      if (toastTimer.current) clearTimeout(toastTimer.current);
      setToast('Aktualizuji záznamy...');
      logChange({ Action: 'rename-project', WorkerName: '', EntryDate: '', OldValue: oldName, NewValue: fields.name });
      try {
        const { updated: count, failed } = await renameProjectInEntries(instance, account, oldName, fields.name);
        console.log(`[handleUpdateProject] Aktualizováno ${count} záznamů, selhalo ${failed}`);
        setEntries(prev => {
          const nextEntries: EntryMap = {};
          for (const [key, entry] of Object.entries(prev)) {
            const raw = entry.ProjectNames || '';
            let parts: string[];
            if (raw.includes('; ')) parts = raw.split('; ').map((n: string) => n.trim()).filter(Boolean);
            else if (raw.includes(', ')) parts = raw.split(', ').map((n: string) => n.trim()).filter(Boolean);
            else parts = raw.trim() ? [raw.trim()] : [];
            const newNames = parts.map((n: string) => n === oldName ? fields.name : n).join(SEP);
            nextEntries[key] = { ...entry, ProjectNames: newNames };
          }
          return nextEntries;
        });
        logChange({ Action: 'rename-result', WorkerName: '', EntryDate: '', OldValue: oldName, NewValue: `${fields.name} (${count} ok, ${failed} selhalo)` });
        if (failed > 0) {
          setRenameRetry({ type: 'project', oldName, newName: fields.name, failed });
        } else {
          setRenameRetry(null);
        }
      } catch (e) {
        setError(`Projekt přejmenován, ale aktualizace plánů selhala: ${String(e)}`);
        setRenameRetry({ type: 'project', oldName, newName: fields.name, failed: -1 });
      } finally {
        setToast(null);
      }
    }

    setProjects(prev => prev.map(p => p.id === id ? {
      ...p, Title: fields.name, SortOrder: fields.sortOrder,
      StartDate: toSP(fields.startDate) ?? undefined,
      DeadlineDate: toSP(fields.deadlineDate) ?? undefined,
    } : p).sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0)));
    showToast(isRename ? `Projekt přejmenován (${fields.name})` : 'Projekt uložen');
  }

  async function handleInitProjectDateColumns() {
    await initProjectDateColumns(instance, account);
    showToast('Datumové sloupce vytvořeny');
  }

  async function handleDeleteProject(id: string) {
    const name = projects.find(p => p.id === id)?.Title ?? '';
    try {
      await deleteProject(instance, account, id);
      setProjects(prev => prev.filter(p => p.id !== id));
      showToast('Projekt smazán');
    } catch (e) {
      setError(`Smazání projektu selhalo: ${String(e)}`);
      return;
    }
    try { logChange({ Action: 'delete-project', WorkerName: '', EntryDate: '', OldValue: name, NewValue: '' }); }
    catch (e) { console.warn('[logChange delete-project]', e); }
  }

  // ─── Worker modal handlers ────────────────────────────────────────────────

  async function handleAddWorker(name: string, sortOrder: number) {
    await addWorker(instance, account, name, sortOrder);
    const fresh = await getWorkers(instance, account);
    setWorkers(fresh);
    showToast('Pracovník přidán');
    try { logChange({ Action: 'add-worker', WorkerName: name, EntryDate: '', OldValue: '', NewValue: name }); } catch (e) { console.warn('[logChange add-worker]', e); }
  }

  async function handleUpdateWorker(id: string, fields: Partial<{ FullName: string; SortOrder: number; IsActive: boolean; oldName: string }>) {
    const oldName = (fields.oldName ?? '').trim();
    const newName = (fields.FullName ?? '').trim();
    const isRename = !!oldName && !!newName && oldName !== newName;

    console.log(`[handleUpdateWorker] id=${id} oldName="${oldName}" newName="${newName}" isRename=${isRename}`);

    const { oldName: _ignored, ...workerFields } = fields;
    await updateWorker(instance, account, id, workerFields as Partial<{ FullName: string; SortOrder: number; IsActive: boolean }>);

    if (isRename) {
      logChange({ Action: 'rename-worker', WorkerName: oldName, EntryDate: '', OldValue: oldName, NewValue: newName });
      try {
        const { updated, failed } = await renameWorkerInEntries(instance, account, oldName, newName);
        console.log(`[handleUpdateWorker] Aktualizováno ${updated} záznamů, selhalo ${failed}`);
        setEntries(prev => {
          const nextEntries: EntryMap = {};
          for (const [key, entry] of Object.entries(prev)) {
            if (entry.WorkerName === oldName) {
              const newKey = `${newName}|${entry.EntryDate}`;
              nextEntries[newKey] = { ...entry, WorkerName: newName };
            } else {
              nextEntries[key] = entry;
            }
          }
          return nextEntries;
        });
        logChange({ Action: 'rename-result', WorkerName: oldName, EntryDate: '', OldValue: oldName, NewValue: `${newName} (${updated} ok, ${failed} selhalo)` });
        if (failed > 0) {
          setRenameRetry({ type: 'worker', oldName, newName, failed });
        } else {
          setRenameRetry(null);
        }
      } catch (e) {
        setError(`Pracovník přejmenován, ale aktualizace plánů selhala: ${String(e)}`);
        setRenameRetry({ type: 'worker', oldName, newName, failed: -1 });
      }
    }

    setWorkers(prev => prev.map(w => w.id === id ? { ...w, ...workerFields, Title: fields.FullName || w.Title } : w));
    showToast(isRename ? `Pracovník přejmenován (${newName})` : 'Pracovník uložen');
  }

  async function handleDeleteWorker(id: string) {
    const w = workers.find(w => w.id === id);
    const name = w?.FullName || w?.Title || '';
    try {
      await deleteWorker(instance, account, id);
      setWorkers(prev => prev.filter(w => w.id !== id));
      showToast('Pracovník smazán');
    } catch (e) {
      setError(`Smazání pracovníka selhalo: ${String(e)}`);
      return;
    }
    try { logChange({ Action: 'delete-worker', WorkerName: name, EntryDate: '', OldValue: name, NewValue: '' }); }
    catch (e) { console.warn('[logChange delete-worker]', e); }
  }

  async function handleRetryRename() {
    if (!renameRetry) return;
    setSaving(true);
    try {
      const { updated, failed } = renameRetry.type === 'project'
        ? await renameProjectInEntries(instance, account, renameRetry.oldName, renameRetry.newName)
        : await renameWorkerInEntries(instance, account, renameRetry.oldName, renameRetry.newName);
      if (renameRetry.type === 'project') {
        setEntries(prev => {
          const nextEntries: EntryMap = {};
          for (const [key, entry] of Object.entries(prev)) {
            const raw = entry.ProjectNames || '';
            let parts: string[];
            if (raw.includes('; ')) parts = raw.split('; ').map((n: string) => n.trim()).filter(Boolean);
            else if (raw.includes(', ')) parts = raw.split(', ').map((n: string) => n.trim()).filter(Boolean);
            else parts = raw.trim() ? [raw.trim()] : [];
            const newNames = parts.map((n: string) => n === renameRetry.oldName ? renameRetry.newName : n).join(SEP);
            nextEntries[key] = { ...entry, ProjectNames: newNames };
          }
          return nextEntries;
        });
      } else {
        setEntries(prev => {
          const nextEntries: EntryMap = {};
          for (const [key, entry] of Object.entries(prev)) {
            if (entry.WorkerName === renameRetry.oldName) {
              const newKey = `${renameRetry.newName}|${entry.EntryDate}`;
              nextEntries[newKey] = { ...entry, WorkerName: renameRetry.newName };
            } else {
              nextEntries[key] = entry;
            }
          }
          return nextEntries;
        });
      }
      if (failed > 0) {
        setRenameRetry(r => r ? { ...r, failed } : null);
        setError(`Zopakování: ${updated} opraveno, ${failed} selhalo.`);
      } else {
        setRenameRetry(null);
        showToast(`Propagace dokončena: ${updated} záznamů aktualizováno`);
      }
    } catch (e) {
      setError(`Chyba při zopakování propagace: ${String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  // ─── Car modal handlers ───────────────────────────────────────────────────

  async function handleAddCar(car: { name: string; color: string; textColor: string; sortOrder: number; notes: string; multiSelect: boolean }) {
    await addCar(instance, account, car);
    const fresh = await getCars(instance, account);
    setCars(fresh);
    showToast('Auto přidáno');
    try { logChange({ Action: 'add-car', WorkerName: '', EntryDate: '', OldValue: '', NewValue: car.name }); } catch (e) { console.warn('[logChange add-car]', e); }
  }

  async function handleUpdateCar(id: string, fields: Partial<{ Title: string; Color: string; TextColor: string; SortOrder: number; Notes: string; MultiSelect: boolean }>) {
    await updateCar(instance, account, id, fields);
    const freshCars = await getCars(instance, account);
    setCars(freshCars);
    showToast('Auto uloženo');
  }

  async function handleDeleteCar(id: string) {
    const name = cars.find(c => c.id === id)?.Title ?? '';
    try {
      await deleteCar(instance, account, id);
      setCars(prev => prev.filter(c => c.id !== id));
      showToast('Auto smazáno');
    } catch (e) {
      setError(`Smazání auta selhalo: ${String(e)}`);
      return;
    }
    try { logChange({ Action: 'delete-car', WorkerName: '', EntryDate: '', OldValue: name, NewValue: '' }); }
    catch (e) { console.warn('[logChange delete-car]', e); }
  }

  // ─── Selection mode ───────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectionMode) { setSelectionMode(false); setSelectedCells(new Set()); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [selectionMode]);

  function toggleCellSelection(cellKey: string) {
    setSelectedCells(prev => { const n = new Set(prev); n.has(cellKey) ? n.delete(cellKey) : n.add(cellKey); return n; });
  }

  async function handleBulkPaste(cellSet?: Set<string>) {
    const cells = cellSet ?? selectedCells;
    if (!clipboard || cells.size === 0) return;

    // Validace názvů ze schránky
    if (clipboard.ProjectNames) {
      const clipNames = clipboard.ProjectNames.split(SEP).filter(Boolean).map(n => n.trim());
      if (clipNames.length > 0) {
        const freshNameSet = await fetchFreshProjects();
        if (freshNameSet) {
          const invalid = clipNames.filter(n => !freshNameSet.has(n));
          if (invalid.length > 0) {
            setError(`Schránka obsahuje neplatné projekty: „${invalid.join('", „')}". Zkopírujte buňku znovu.`);
            return;
          }
        }
      }
    }

    setSaving(true);
    try {
      const results = await Promise.all(Array.from(cells).map(async cellKey => {
        const lastPipe = cellKey.lastIndexOf('|');
        const workerName = cellKey.slice(0, lastPipe);
        const date = cellKey.slice(lastPipe + 1);
        const worker = workers.find(w => (w.FullName || w.Title) === workerName);
        if (!worker) return null;
        const existing = entries[cellKey];
        const entryYear = new Date(date).getFullYear();
        const weekNumber = getISOWeek(new Date(date));
        const payload = {
          id: existing?.id, WorkerId: Number(worker.id),
          WorkerName: workerName, EntryDate: date,
          ProjectNames: clipboard.ProjectNames, CarId: clipboard.CarId,
          CarColor: clipboard.CarColor, Year: entryYear, WeekNumber: weekNumber,
        };
        const newId = await upsertPlannerEntry(instance, account, payload);
        logChange({
          Action: 'bulk-paste',
          WorkerName: workerName,
          EntryDate: date,
          OldValue: existing?.ProjectNames ?? '',
          NewValue: clipboard.ProjectNames,
        });
        return { cellKey, entry: { ...payload, id: newId } };
      }));
      setEntries(prev => {
        const updated = { ...prev };
        results.forEach(r => { if (r) updated[r.cellKey] = r.entry; });
        return updated;
      });
      showToast(`Vloženo do ${cells.size} buněk`);
      setSelectionMode(false);
      setSelectedCells(new Set());
    } catch (e) { setError(String(e)); } finally { setSaving(false); }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  const activeWorkers = workers.filter(w => w.IsActive !== false);

  type WeekBlock = { week: number; weekYear: number; dates: { date: Date; dateStr: string; dayIdx: number }[] };
  const weekBlocks: WeekBlock[] = getWeeksForMonth(year, month).map(w => {
    const wYear = (month === 12 && w === 1) ? year + 1 : (month === 1 && w > 50) ? year - 1 : year;
    return { week: w, weekYear: wYear, dates: getWeekDates(wYear, w).map((d, i) => ({ date: d, dateStr: formatDate(d), dayIdx: i })) };
  });

  const modalBtn: React.CSSProperties = {
    padding: '5px 14px', border: 'none', borderRadius: 6, background: 'rgba(255,255,255,0.2)',
    color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500,
  };

  return (
    <div style={{ fontFamily: 'Segoe UI, Arial, sans-serif', minHeight: '100vh', background: '#f4f4f4', '--sticky-top': `${stickyTop}px` } as React.CSSProperties}>
      <div ref={topbarRef} style={{ position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Header */}
      <div style={{ background: LR_GREEN, color: '#fff' }}>
        <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 700, fontSize: 20, letterSpacing: 1 }}>Lee Row s.r.o.</span>
          <span style={{ fontSize: 15, opacity: 0.85 }}>Plánování {year}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            {!isReadOnly && saving && <span style={{ fontSize: 12, opacity: 0.8, marginRight: 8 }}>Ukládám…</span>}
            {error && <span style={{ fontSize: 12, color: '#ffe', marginRight: 8, cursor: 'pointer' }} onClick={() => setError(null)}>⚠ {error}</span>}
            {renameRetry && !saving && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#ffe', marginRight: 8 }}>
                ⚠ {renameRetry.failed > 0 ? `${renameRetry.failed} záznam${renameRetry.failed > 4 ? 'ů' : renameRetry.failed > 1 ? 'y' : ''} se nepodařilo přejmenovat` : 'Propagace přejmenování selhala'} ({renameRetry.oldName} → {renameRetry.newName})
                <button onClick={handleRetryRename} style={{ padding: '2px 10px', background: '#fff', color: '#c0392b', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700, fontSize: 11 }}>Zopakovat propagaci</button>
                <button onClick={() => setRenameRetry(null)} style={{ background: 'none', border: 'none', color: '#ffe', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 0, opacity: 0.7 }}>×</button>
              </span>
            )}
            {isReadOnly && <span style={{ fontSize: 12, opacity: 0.7, marginRight: 4 }}>Přehled (read-only)</span>}
            {!isReadOnly && <button style={modalBtn} onClick={() => setShowProjectsModal(true)}>Projekty</button>}
            {!isReadOnly && <button style={modalBtn} onClick={() => setShowWorkersModal(true)}>Pracovníci</button>}
            {!isReadOnly && <button style={modalBtn} onClick={() => setShowCarsModal(true)}>Auta</button>}
            {!isReadOnly && <button style={modalBtn} onClick={() => setShowPrintModal(true)}>Tisk</button>}
            {!isReadOnly && <button style={modalBtn} onClick={() => setShowHistoryDrawer(true)}>Historie</button>}
          </div>
        </div>
        {!isReadOnly && selectionMode && (
          <div style={{ background: 'rgba(0,0,0,0.25)', borderTop: '1px solid rgba(255,255,255,0.2)', padding: '7px 16px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Vybráno {selectedCells.size} buněk</span>
            <span style={{ opacity: 0.5 }}>—</span>
            <button
              onClick={() => handleBulkPaste()}
              disabled={!clipboard || selectedCells.size === 0 || saving}
              style={{ padding: '4px 14px', background: clipboard && selectedCells.size > 0 ? '#fff' : 'rgba(255,255,255,0.3)', color: LR_GREEN, border: 'none', borderRadius: 5, cursor: clipboard && selectedCells.size > 0 ? 'pointer' : 'default', fontWeight: 700, fontSize: 13 }}>
              Vložit zkopírovaný obsah
            </button>
            <button
              onClick={() => { setSelectionMode(false); setSelectedCells(new Set()); }}
              style={{ padding: '4px 14px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>
              Zrušit výběr
            </button>
            {!clipboard && <span style={{ fontSize: 12, opacity: 0.7 }}>Nejprve zkopírujte buňku</span>}
          </div>
        )}
      </div>

      {/* Month tabs */}
      <div style={{ display: 'flex', background: '#fff', borderBottom: '2px solid #e0e0e0', paddingLeft: 8, paddingTop: 6 }}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
          <button key={m} data-testid={`month-tab-${m}`} onClick={() => setMonth(m)} style={{
            padding: '7px 14px', border: 'none', borderBottom: m === month ? `3px solid ${LR_GREEN}` : '3px solid transparent',
            background: 'none', cursor: 'pointer', fontWeight: m === month ? 700 : 400,
            color: m === month ? LR_GREEN : '#555', fontSize: 13,
          }}>
            {MONTH_NAMES[m]}
          </button>
        ))}
      </div>

      {/* Car legend */}
      {cars.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '7px 16px', background: '#fff', borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: '#666', lineHeight: '20px' }}>AUTA:</span>
          {cars.map(c => (
            <span key={c.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                <span style={{ width: 14, height: 14, borderRadius: 3, background: c.Color, border: '1px solid #ccc', display: 'inline-block' }} />
                {c.Title}
              </span>
              {c.Notes?.trim() && (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#e74c3c', textAlign: 'center', maxWidth: 120 }}>{c.Notes}</span>
              )}
            </span>
          ))}
        </div>
      )}
      </div>{/* end topbar */}

      {/* Weekly blocks */}
      <div style={{ padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Načítám data…</div>
        ) : weekBlocks.map(wb => {
          // Milestone row: projects whose StartDate or DeadlineDate falls in this week
          const milestones: { dateStr: string; project: Project; type: 'start' | 'deadline' }[] = [];
          wb.dates.forEach(({ dateStr }) => {
            projects.forEach(p => {
              if (p.StartDate && p.StartDate.slice(0, 10) === dateStr) milestones.push({ dateStr, project: p, type: 'start' });
              if (p.DeadlineDate && p.DeadlineDate.slice(0, 10) === dateStr) milestones.push({ dateStr, project: p, type: 'deadline' });
            });
          });
          const hasMilestones = milestones.length > 0;

          return (
          <div key={wb.week}>
            <div style={{ position: 'sticky', top: 'var(--sticky-top)', zIndex: 10, background: LR_GREEN, color: '#fff', padding: '5px 12px', fontWeight: 700, fontSize: 13, borderRadius: '6px 6px 0 0', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>Týden {wb.week}</span>
              <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.85 }}>
                {formatDateCZ(wb.dates[0].date)} – {formatDateCZ(wb.dates[6].date)}
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
            {hasMilestones && (
              <table style={{ borderCollapse: 'collapse', width: 'max-content', background: '#5a8a2e' }}>
                <tbody>
                  <tr>
                    <td style={{ width: NAME_W, minWidth: NAME_W, borderRight: '2px solid rgba(255,255,255,0.2)' }} />
                    {wb.dates.map(({ dateStr }) => {
                      const dayMilestones = milestones.filter(m => m.dateStr === dateStr);
                      return (
                        <td key={dateStr} style={{ width: CELL_W, minWidth: CELL_W, padding: '3px 5px', borderRight: '1px solid rgba(255,255,255,0.15)', verticalAlign: 'top' }}>
                          {dayMilestones.map((m, i) => (
                            <div key={i} style={{ lineHeight: '14px' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: CELL_W - 10 }}>{m.project.Title}</div>
                              <div style={{ fontSize: 10, color: m.type === 'deadline' ? '#ffaaaa' : '#000', fontWeight: 600 }}>{m.type === 'start' ? 'Start' : 'Deadline'}</div>
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            )}
            <table style={{ borderCollapse: 'collapse', background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
              <thead>
                <tr>
                  <th style={{ width: NAME_W, minWidth: NAME_W, background: '#f0f0f0', borderRight: '2px solid #ccc', borderBottom: '2px solid #ccc', padding: '4px 10px', textAlign: 'left', fontSize: 12, color: '#666' }}>
                    Pracovník
                  </th>
                  {wb.dates.map(({ date, dateStr, dayIdx }) => {
                    const isWeekend = dayIdx >= 5;
                    const isHoliday = holidays.has(dateStr);
                    return (
                      <th key={dateStr} style={{ width: CELL_W, minWidth: CELL_W, background: isWeekend ? '#e8e8e8' : '#f0f0f0', borderRight: '1px solid #ddd', borderBottom: '2px solid #ccc', padding: '3px 4px', textAlign: 'center', fontSize: 11, color: '#444', fontWeight: isWeekend ? 400 : 600 }}>
                        <div>{DAY_NAMES[dayIdx].slice(0, 2)}</div>
                        <div style={{ color: '#888', fontWeight: 400 }}>{formatDateCZ(date)}</div>
                        {isHoliday && <div style={{ color: '#e74c3c', fontWeight: 400, fontSize: 10 }}>svátek</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {activeWorkers.map((worker, rowIdx) => (
                  <tr key={worker.id} style={{ background: rowIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ width: NAME_W, minWidth: NAME_W, padding: '0 10px', fontSize: 13, fontWeight: 500, borderRight: '2px solid #ccc', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>
                      {worker.FullName || worker.Title}
                    </td>
                    {wb.dates.map(({ dateStr, dayIdx }) => {
                      const isWeekend = dayIdx >= 5;
                      const entry = entries[entryKey(worker.FullName || worker.Title, dateStr)];
                      return (
                        <td key={dateStr} data-testid={`cell|${worker.FullName || worker.Title}|${dateStr}`} style={{ padding: 0, borderRight: '1px solid #ddd', borderBottom: '1px solid #eee' }}>
                          <PlannerCell
                            entry={entry} projects={projects} cars={cars} isWeekend={isWeekend} isReadOnly={isReadOnly}
                            selectionMode={selectionMode}
                            isSelected={selectedCells.has(entryKey(worker.FullName || worker.Title, dateStr))}
                            stickyTop={stickyTop}
                            onToggleSelect={() => toggleCellSelection(entryKey(worker.FullName || worker.Title, dateStr))}
                            onProjectsChange={names => handleProjectsChange(worker, dateStr, wb.week, names)}
                            onContextMenu={(x, y) => {
                              const wName = worker.FullName || worker.Title;
                              const e = entries[entryKey(wName, dateStr)];
                              const currentProjects = new Set((e?.ProjectNames ?? '').split(SEP).filter(Boolean));
                              const blockedCarIds = new Set<number>();
                              activeWorkers.forEach(aw => {
                                const awName = aw.FullName || aw.Title;
                                if (awName === wName) return;
                                const awEntry = entries[entryKey(awName, dateStr)];
                                if (!awEntry?.CarId) return;
                                const carObj = cars.find(c => Number(c.id) === awEntry.CarId);
                                if (carObj?.MultiSelect) return;
                                const shared = (awEntry.ProjectNames ?? '').split(SEP).filter(Boolean).some(p => currentProjects.has(p));
                                if (!shared) blockedCarIds.add(awEntry.CarId);
                              });
                              const availableCars = cars.filter(c => !blockedCarIds.has(Number(c.id)));
                              setContextMenu({ workerId: worker.id, workerName: wName, date: dateStr, weekNumber: wb.week, x, y, availableCars });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        );
        })}
      </div>

      {/* Modals */}
      {showProjectsModal && (
        <ProjectsModal projects={projects} onAdd={handleAddProject} onUpdate={handleUpdateProject} onDelete={handleDeleteProject} onInitDateColumns={handleInitProjectDateColumns} onClose={() => setShowProjectsModal(false)} />
      )}
      {showWorkersModal && (
        <WorkersModal workers={workers} onAdd={handleAddWorker} onUpdate={handleUpdateWorker} onDelete={handleDeleteWorker} onClose={() => setShowWorkersModal(false)} />
      )}
      {showCarsModal && (
        <CarsModal cars={cars} instance={instance} account={account} onAdd={handleAddCar} onUpdate={handleUpdateCar} onDelete={handleDeleteCar} onClose={() => setShowCarsModal(false)} showToast={showToast} />
      )}

      {/* Print modal */}
      {showPrintModal && (
        <PrintModal workers={workers} cars={cars} entries={entries} holidays={holidays} instance={instance} account={account} year={year} onClose={() => setShowPrintModal(false)} />
      )}

      {/* History drawer */}
      <HistoryDrawer
        open={showHistoryDrawer}
        instance={instance}
        account={account}
        onClose={() => setShowHistoryDrawer(false)}
      />

      {/* Car context menu */}
      {contextMenu && (
        <CarContextMenu
          cars={contextMenu.availableCars} x={contextMenu.x} y={contextMenu.y}
          hasClipboard={!!clipboard}
          stickyTop={stickyTop}
          onCopy={handleCopy}
          onPaste={() => {
            if (contextMenu) {
              const contextCellKey = entryKey(contextMenu.workerName, contextMenu.date);
              if (selectedCells.size > 0) {
                handleBulkPaste(new Set(Array.from(selectedCells).concat(contextCellKey)));
              } else {
                handlePaste();
              }
            }
            setContextMenu(null);
          }}
          onStartSelection={() => { setSelectionMode(true); setSelectedCells(new Set()); }}
          onSelect={car => {
            handleCarChange(contextMenu.workerId, contextMenu.workerName, contextMenu.date, contextMenu.weekNumber, car);
            setContextMenu(null);
          }}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Toast */}
      {toast && <Toast message={toast} />}
    </div>
  );
}
